import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Menu } from "lucide-react";
import api, { resolveAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function ClienteSwitcher({ clienteId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [status, setStatus] = useState("loading");
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setStatus("loading");
    api.get("/clientes").then(({ data }) => {
      if (!active) return;
      setClientes(data);
      setStatus("ready");
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [open, tentativa]);

  const ordenados = [...clientes]
    .sort((a, b) => (a.empresa || "").localeCompare(b.empresa || "", "pt-BR"));

  const abrirCliente = (cliente) => {
    setOpen(false);
    if (cliente.id !== clienteId) navigate(`/producao/clientes/${cliente.id}`);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" data-testid="clientes-menu-trigger" aria-label="Trocar cliente"
          className="h-11 shrink-0 gap-2 rounded-xl border-[var(--studio-border)] bg-[var(--studio-card)] text-[var(--studio-text)] hover:bg-[var(--studio-elevated)]">
          <Menu size={18} aria-hidden="true" /> Clientes
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} collisionPadding={12} data-testid="clientes-menu" aria-label="Clientes" aria-busy={status === "loading"}
        className="w-80 max-w-[calc(100vw-1.5rem)] max-h-[min(60vh,var(--radix-dropdown-menu-content-available-height))] overscroll-contain rounded-xl border-[var(--studio-border)] bg-[var(--studio-card)] p-2 text-[var(--studio-text)] shadow-xl">
        {status === "loading" && <p role="status" className="px-3 py-5 text-sm text-[var(--studio-secondary)]">Carregando clientes…</p>}
        {status === "error" && <>
          <p role="alert" className="px-3 py-4 text-sm text-[var(--studio-secondary)]">Não foi possível carregar os clientes.</p>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); setTentativa((value) => value + 1); }} className="min-h-11 cursor-pointer rounded-lg px-3 font-semibold focus:bg-[var(--studio-elevated)] focus:text-[var(--studio-text)]">Tentar novamente</DropdownMenuItem>
        </>}
        {status === "ready" && ordenados.length === 0 && <p role="status" className="px-3 py-5 text-sm text-[var(--studio-secondary)]">Nenhum cliente cadastrado.</p>}
        {status === "ready" && ordenados.map((cliente) => {
          const atual = cliente.id === clienteId;
          return <DropdownMenuItem key={cliente.id} data-testid={`clientes-menu-item-${cliente.id}`} aria-current={atual ? "page" : undefined}
            onSelect={() => abrirCliente(cliente)} className={`min-h-14 cursor-pointer gap-3 rounded-lg border px-3 py-3 focus:bg-[var(--studio-elevated)] focus:text-[var(--studio-text)] ${atual ? "border-[var(--studio-primary)] bg-[var(--studio-panel)]" : "border-transparent"}`}>
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={resolveAssetUrl(cliente.logo)} alt="" />
              <AvatarFallback className="bg-[var(--studio-elevated)] text-[var(--studio-primary)]">{cliente.empresa?.[0] || "C"}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 break-words text-sm font-semibold">{cliente.empresa}</span>
            {atual && <Check size={16} aria-label="Cliente atual" className="text-[var(--studio-primary)]" />}
          </DropdownMenuItem>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
